import { useCallback, useMemo } from 'react';
import { uniq } from 'lodash-es';
import { Controller, useFormContext } from 'react-hook-form';
import { DateValue, toZoned } from '@internationalized/date';
import { IoMdTime } from 'react-icons/io';
import { Input, SelectItem, TmdxDatepicker, TmdxSelect } from '@ds';
import { AddressSearch } from '@ui';
import { useLegFormContext } from '@lp/features/RoutePlanner/components/RouteLegForm/context/LegFormContext';
import { BaseAccommodationLegSchemaValues } from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import { useCustomLocationAddress } from '@lp/features/RoutePlanner/components/RouteLegForm/hooks';
import { useRouteLocations } from '@lp/features/RoutePlanner/hooks/useRouteLocations';
import { renderSelectItem, validateDateTimeValue } from '@/utils/helpers';
import { SelectItemType } from '@/types';
import { useWatchFields } from '@/hooks/useWatchFields';

export const AccommodationFormInputs = () => {
  const { getLocationById } = useRouteLocations();
  const { isMainDataEdit, getSegmentLocations } = useLegFormContext();
  const { control, setValue } = useFormContext<BaseAccommodationLegSchemaValues>();
  const {
    arrivalLocation: { displayName, timezone },
    resources,
    rooms,
  } = useWatchFields<BaseAccommodationLegSchemaValues>(['arrivalLocation', 'resources', 'rooms'] as const);

  const { addressList, onAddressSelect, onAddressInputChange } = useCustomLocationAddress({ type: 'arrivalLocation' });

  const countries = useMemo(() => {
    const locations = getSegmentLocations('', true);

    return uniq(locations.map((l) => getLocationById(l.id)?.location.country).filter(Boolean)).map(
      (c) =>
        ({
          value: c,
          name: c,
        }) as SelectItemType,
    );
  }, [getSegmentLocations, getLocationById]);

  const onDateChange = useCallback(
    (date: DateValue | null, dateType: 'checkInAt' | 'checkOutAt' = 'checkInAt') => {
      const dateValue = validateDateTimeValue(date);
      const zonedDateValue = dateValue
        ? toZoned(dateValue, timezone || 'UTC').set({ hour: 12, minute: 0, second: 0 })
        : date;
      const dateString = dateValue && zonedDateValue ? zonedDateValue.toString() : null;

      setValue(`${dateType}Value`, zonedDateValue);
      setValue(dateType, dateString || '');

      return dateString || '';
    },
    [setValue, timezone],
  );

  const roomsSelectList = useMemo(() => {
    const maxCounter = Math.max(resources.length, rooms, 1);

    return Array(maxCounter)
      .fill('1')
      .map((_, i) => ({
        value: i + 1,
        name: `${i + 1}`,
      }));
  }, [resources, rooms]);

  const isCustomWithCountriesSelect = countries.length > 1;

  return (
    <>
      <div
        className="grid gap-4 w-full relative z-20"
        style={{
          gridTemplateColumns: `16rem ${isCustomWithCountriesSelect ? '7rem' : ''} minmax(0, 1fr)`,
          gridTemplateRows: '1fr',
        }}
      >
        <Controller
          name="hotelName"
          control={control}
          render={({ field, fieldState: { invalid } }) => (
            <Input
              aria-label="Hotel"
              label="Hotel"
              placeholder="Hotel name"
              isDisabled={!isMainDataEdit}
              isInvalid={invalid}
              {...field}
            />
          )}
        />
        {countries.length > 1 && (
          <Controller
            name="arrivalLocation.country"
            control={control}
            render={({ field: { ref, value, ...field }, fieldState: { invalid } }) => (
              <TmdxSelect
                placeholder="Choose an option"
                aria-label="Country"
                label="Country"
                selectedKeys={value ? [value] : []}
                isInvalid={invalid}
                isDisabled={!isMainDataEdit}
                disallowEmptySelection
                items={countries}
                {...field}
              >
                {renderSelectItem}
              </TmdxSelect>
            )}
          />
        )}
        <Controller
          name="arrivalLocation.id"
          control={control}
          render={({ field: { ref, value, onChange, ...field }, fieldState: { invalid } }) => (
            <AddressSearch
              addressList={addressList}
              onAddressSelect={onAddressSelect}
              onAddressInputChange={onAddressInputChange}
              value={displayName || ''}
              onChange={onChange}
              isInvalid={invalid}
              isDisabled={!isMainDataEdit}
              inputProps={{
                placeholder: 'Search address',
                label: 'Address',
                'aria-label': 'Address',
              }}
              wrapperProps={{
                'aria-label': 'Search Address',
              }}
              {...field}
            />
          )}
        />
      </div>
      <div className="flex gap-4 w-full">
        <Controller
          name="checkInAtValue"
          control={control}
          render={({ field: { value, onChange, ...field }, fieldState: { invalid } }) => (
            <TmdxDatepicker
              size="md"
              aria-label="requiredCheckInDate"
              label="Check-in Date *"
              hideTimeZone
              hourCycle={24}
              isDisabled={!isMainDataEdit}
              showMonthAndYearPickers={false}
              granularity="day"
              value={value}
              onChange={(date: DateValue | null) => onDateChange(date, 'checkInAt')}
              isInvalid={invalid}
              {...field}
              endContent={
                <div className="pointer-events-none flex items-center">
                  <span className="text-default-400 text-small">
                    <IoMdTime size={16} />
                  </span>
                </div>
              }
            />
          )}
        />
        <Controller
          name="checkOutAtValue"
          control={control}
          render={({ field: { value, onChange, ...field }, fieldState: { invalid } }) => (
            <TmdxDatepicker
              size="md"
              aria-label="requiredCheckOutDate"
              label="Check-out Date *"
              hideTimeZone
              hourCycle={24}
              isDisabled={!isMainDataEdit}
              showMonthAndYearPickers={false}
              granularity="day"
              value={value}
              onChange={(date: DateValue | null) => onDateChange(date, 'checkOutAt')}
              isInvalid={invalid}
              {...field}
              endContent={
                <div className="pointer-events-none flex items-center">
                  <span className="text-default-400 text-small">
                    <IoMdTime size={16} />
                  </span>
                </div>
              }
            />
          )}
        />
        <Controller
          name="rooms"
          control={control}
          render={({ field: { value, onChange } }) => (
            <TmdxSelect
              placeholder="Choose an option"
              label="Rooms"
              aria-label="Rooms"
              selectedKeys={value ? [String(value)] : []}
              disallowEmptySelection
              isDisabled={!isMainDataEdit}
              onChange={(e) => onChange(Number(e.target.value))}
            >
              {roomsSelectList.map((e) => (
                <SelectItem key={e.value}>{e.name}</SelectItem>
              ))}
            </TmdxSelect>
          )}
        />
        <Controller
          name="bookingNumber"
          control={control}
          render={({ field, fieldState: { invalid, error } }) => (
            <Input
              size="md"
              aria-label="Booking Number"
              label="Booking Number"
              placeholder="Booking Number"
              isInvalid={invalid}
              errorMessage={error?.message}
              {...field}
            />
          )}
        />
      </div>
    </>
  );
};
